/**
 * Email suppression service — Phase 1 reads only. Phase 5 will add the
 * admin override RPC for unsuppressing.
 *
 * RLS gates SELECT on email_suppressions to is_internal_staff(), so any
 * authenticated staff user can call these — the rendering surfaces
 * (ContactDetail badge, Contacts list filter, segment criteria) all
 * already require internal-staff access.
 */

import { supabase } from "./supabase";
import type { EmailSuppression } from "../types/emailSuppression.types";

/**
 * Look up a single suppression record by email. Returns null when no
 * record exists — that includes "actively delivering" and "never seen
 * a bounce/unsub event."
 *
 * Email is normalized lower(trim) before lookup. Pass the contact's
 * raw email; we do the normalization here.
 */
export async function getEmailSuppression(
  email: string | null | undefined,
): Promise<EmailSuppression | null> {
  if (!email || !email.trim()) {
    return null;
  }
  const emailLower = email.toLowerCase().trim();
  const { data, error } = await supabase
    .from("email_suppressions")
    .select(
      "email_lower, is_suppressed, reason, reason_detail, first_suppressed_at, last_event_at, soft_bounce_count, source, suppressed_by, notes, created_at, updated_at",
    )
    .eq("email_lower", emailLower)
    .maybeSingle();

  if (error) {
    // RLS denial for non-staff returns an error rather than empty —
    // treat as "no data visible" so the UI degrades cleanly.
    return null;
  }
  return (data as EmailSuppression | null) ?? null;
}

/**
 * Cheap existence check for the contact edit form. Returns true if the
 * target email is currently in a suppressed state — so the form can
 * warn the operator before saving the change.
 *
 * Sub-threshold soft-bounce tracker rows return false (is_suppressed
 * is the truth signal, not "row exists").
 */
export async function isEmailSuppressed(
  email: string | null | undefined,
): Promise<boolean> {
  const suppression = await getEmailSuppression(email);
  return suppression?.is_suppressed === true;
}

/**
 * Recent event history for a given email. Used by the ContactDetail
 * "Email status" panel to show the audit trail (most recent first).
 * Limited to 20 events by default — enough to see the pattern that
 * caused the current state, not enough to clog the panel.
 */
export interface EmailSuppressionEvent {
  id: string;
  email_lower: string;
  event_type: string;
  event_time: string;
  detail: Record<string, unknown> | null;
  outcome: string;
  resulting_reason: string | null;
  resulting_soft_count: number | null;
  resulting_is_suppressed: boolean | null;
  actor_id: string | null;
  recorded_at: string;
}

export async function getEmailSuppressionEvents(
  email: string | null | undefined,
  limit = 20,
): Promise<EmailSuppressionEvent[]> {
  if (!email || !email.trim()) {
    return [];
  }
  const emailLower = email.toLowerCase().trim();
  const { data, error } = await supabase
    .from("email_suppression_events")
    .select(
      "id, email_lower, event_type, event_time, detail, outcome, resulting_reason, resulting_soft_count, resulting_is_suppressed, actor_id, recorded_at",
    )
    .eq("email_lower", emailLower)
    .order("event_time", { ascending: false })
    .limit(limit);

  if (error) {
    return [];
  }
  return (data ?? []) as EmailSuppressionEvent[];
}
